/**
 * reviewQueueResource — the operator review-queue inbox (#1416 convergence of
 * the former bespoke ReviewQueuePage), authored against the resource-kit v2
 * model/view contracts and consumed directly as a `ResourceBundle` by
 * `ResourcePage` (#1805 slice A3b).
 *
 * The disposition-required resolve flow — a Select + note the pre-v2 static
 * `rowActions` confirm could not express — is the first consumer of the
 * rowActions v2 custom-confirm surface. The count badge + 30s cadence ride the
 * `listTitleAdornment` / `refetchInterval` host seams.
 *
 * The model and the two views lift to MODULE scope; the host does not, and
 * cannot. `rowActions` and `detail.actions` are the SAME resolve action object
 * — one busy claim per row across the table cell and the drawer footer — and
 * that action closes over the page's `QueryClient` so it can invalidate the
 * per-order trace views alongside the resource list. So
 * `buildReviewQueueResource(queryClient)` survives the migration unchanged in
 * shape: it builds the action, then the host around it.
 *
 * Both cell projections moved to `../widgets.tsx`, along with the badge tables
 * and the `details_json` readers they need. Read that file's header before
 * touching either: the widget module may not reach a service, and the
 * `csvText` each widget declares is what keeps `kind` exporting its RAW wire
 * token instead of the enum label a widget-less `enum` column would produce.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import { Inbox } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  EntityLink,
  CopyButton,
  IdCell,
} from '@resource-kit/ui/react';
import {
  rowAction,
  type ResourceBundle,
  type DataTableParams,
} from '@resource-kit/controllers/react';
import {
  type EnumOption,
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import { JsonPanel } from '@components/ui/JsonPanel';
import { DisplayTable } from '@components/ui/DisplayTable';
import i18n from '@/i18n';
import { ApiError } from '@/lib/api';
import { ERROR_CODES } from '@lib/errorCodes';
import { closedEnumLookup } from '@lib/closedEnumGuard';
import { queryKeys } from '@lib/queryKeys';
import { todayInMerchantTz } from '@lib/datetime';
import type { ListResult } from '@resource-kit/controllers';
import { REVIEW_QUEUE_FILTERS, REVIEW_QUEUE_SORTS } from '@/services/filters.generated';
import {
  POST_DISPATCH_CANCELLATION_CAUSE,
  REVIEW_QUEUE_KIND,
  REVIEW_QUEUE_STATUS,
  REVIEW_QUEUE_STATUS_FILTER,
  pendingViewParams,
  reviewQueueAPI,
  type PostDispatchCancellationCause,
  type ReviewQueueItem,
  type ReviewQueueKind,
} from '@/services/review-queue';
import { lineItemProductId, lineItemTitle, parseLineItemsJson } from '@lib/line-items';
import { orderAPI } from '@/services/orders';
import { detailString, detailStringArray, parseDetails } from '../widgets';

/**
 * Re-exported so `@features/admin/pages/reviewQueueResource` stays the import
 * path for the two summary helpers. They live in `../widgets.tsx` now because
 * the cell projections need them and a widget module may not reach a service —
 * see that file's header.
 */
export { localizedSummary, typeBadgeLabel } from '../widgets';

// ─── Resolve confirm surface (rowActions v2 custom confirm) ──────────────────

/** The disposition Select + note collected by the resolve action's custom
 *  confirm surface. `note` is trimmed to `undefined` when blank. */
export interface ResolvePayload {
  resolution: string;
  note?: string;
}

const resolvePayloadSchema = z.object({
  resolution: z.string().min(1),
  note: z.string().optional(),
});

const RESOLUTION_NONE = '__none__' as const;

const RESOLVE_OPTION_I18N = {
  [REVIEW_QUEUE_KIND.POST_CUTOFF_ORDER_CHANGE]: {
    no_change_needed: 'reviewQueue.resolveOption.post_cutoff_order_change.no_change_needed',
    contents_updated: 'reviewQueue.resolveOption.post_cutoff_order_change.contents_updated',
    contacted_customer: 'reviewQueue.resolveOption.post_cutoff_order_change.contacted_customer',
  },
  [REVIEW_QUEUE_KIND.POST_DISPATCH_CANCELLATION]: {
    comp_full: 'reviewQueue.resolveOption.post_dispatch_cancellation.comp_full',
    comp_partial: 'reviewQueue.resolveOption.post_dispatch_cancellation.comp_partial',
    chase_customer: 'reviewQueue.resolveOption.post_dispatch_cancellation.chase_customer',
    no_action_needed: 'reviewQueue.resolveOption.post_dispatch_cancellation.no_action_needed',
  },
  [REVIEW_QUEUE_KIND.DISPATCH_FAILED]: {
    retried_ok: 'reviewQueue.resolveOption.dispatch_failed.retried_ok',
    dispatched_manually: 'reviewQueue.resolveOption.dispatch_failed.dispatched_manually',
    no_action_needed: 'reviewQueue.resolveOption.dispatch_failed.no_action_needed',
  },
} as const satisfies Record<ReviewQueueKind, Record<string, string>>;
const LEGACY_RESOLVE_OPTION = {
  id: 'acknowledged_legacy',
  i18nKey: 'reviewQueue.resolveOption.acknowledged_legacy',
} as const;

const resolveOptionI18nFor = closedEnumLookup<
  ReviewQueueKind,
  Readonly<Record<string, string>> | undefined
>(RESOLVE_OPTION_I18N, undefined);

function resolveOptionLabel(
  item: ReviewQueueItem,
  opt: ReviewQueueItem['resolveOptions'][number],
  t: TFunction,
): string {
  if (opt.id === LEGACY_RESOLVE_OPTION.id) return t(LEGACY_RESOLVE_OPTION.i18nKey);
  const key = resolveOptionI18nFor(item.kind)?.[opt.id];
  if (key) return t(key);
  console.warn(
    `[review-queue] Unknown resolve option '${opt.id}' for kind '${item.kind}' — the ` +
      `resolve-option closed set needs an entry, or the wire payload is malformed. ` +
      `Falling back to the backend label.`,
  );
  return opt.label;
}

function ResolveConfirm({
  item,
  submit,
  cancel,
}: {
  item: ReviewQueueItem;
  submit: (payload: ResolvePayload) => void;
  cancel: () => void;
}) {
  const { t } = useTranslation();
  const [resolution, setResolution] = useState<string>(RESOLUTION_NONE);
  const [note, setNote] = useState('');
  const canConfirm = resolution !== RESOLUTION_NONE;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">{t('reviewQueue.row.resolve')}</h3>
      <div className="space-y-3">
        <Select
          value={resolution}
          onValueChange={setResolution}
          // `items` feeds Select.Value the label; without it the trigger
          // shows the raw option id until the popup has mounted once.
          items={Object.fromEntries(
            item.resolveOptions.map((opt) => [opt.id, resolveOptionLabel(item, opt, t)]),
          )}
        >
          <SelectTrigger className="w-full" aria-label={t('reviewQueue.row.resolutionLabel')}>
            <SelectValue placeholder={t('reviewQueue.row.resolutionPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {item.resolveOptions.map((opt) => (
              <SelectItem key={opt.id} value={opt.id}>
                {resolveOptionLabel(item, opt, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('reviewQueue.row.notePlaceholder')}
          maxLength={500}
          aria-label={t('reviewQueue.row.noteLabel')}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={cancel}>
          {t('ui.cancel')}
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!canConfirm}
          onClick={() => submit({ resolution, note: note.trim() || undefined })}
        >
          {t('reviewQueue.row.confirmResolve')}
        </Button>
      </div>
    </div>
  );
}

// ─── Drawer: related-entity links (#1465) ────────────────────────────────────

/** The internal fulfillment-job ids referenced by a row's detail: the single
 *  `fulfillment_job_id` (post_dispatch_cancellation / dispatch_failed) plus the
 *  `job_ids[]` array (post_cutoff_order_change), deduped. */
function relatedJobIds(detail: Record<string, unknown>): string[] {
  const single = detailString(detail, 'fulfillment_job_id');
  const many = detailStringArray(detail, 'job_ids');
  return [...new Set([...(single ? [single] : []), ...many])];
}

/**
 * Render the drawer's "related entities" block — the three references an
 * operator acts on, each in the form they can actually act on (#1997):
 *
 *   ORDER — the operator-facing NAME (`#88845HGS`), linked to the internal
 *     order page. Both come off the ITEM, not the detail bag: only
 *     `post_cutoff_order_change` records an internal `order_id` in its details,
 *     so keying off the bag left the two job-scoped kinds — the ones this inbox
 *     is mostly made of — with no link and a bare Shopify number. The backend
 *     now resolves `order_id`/`order_name` for every kind (joining
 *     `orders.shopify_id` when the details carry only the external key).
 *     An unresolved order degrades to the plain Shopify id, deliberately NOT
 *     linked: there is no internal uuid, so there is no page to open.
 *
 *   JOB — a shortened uuid. `truncate='uuid'` is the DETAIL-surface budget:
 *     it shortens a uuid and leaves any other shape whole, and `IdCell` keeps
 *     the full value in the tooltip and on the copy button either way.
 *
 *   DRIVER TASK — the SHORT id when the row carries one, because that is what
 *     the Onfleet dashboard displays; the long api id remains the fallback for
 *     rows enqueued before the short id was recorded. Still not a link — there
 *     is no internal page for a task.
 *
 * Returns null when the row references none of the three.
 */
function RelatedEntities({
  item,
  detail,
}: {
  item: ReviewQueueItem;
  detail: Record<string, unknown>;
}) {
  const shopifyOrderId = detailString(detail, 'shopify_order_id');
  const orderDisplay = item.order_name ?? shopifyOrderId;
  // Wire field first, detail bag second. The detail fallback is not redundant:
  // the two halves of this app deploy separately, so a frontend that ships
  // ahead of the backend would otherwise DROP the order link on exactly the
  // kind that has always had one (`post_cutoff_order_change` records
  // `order_id` in its details) — a regression of #1465 lasting until the
  // backend catches up.
  const orderId = item.order_id ?? detailString(detail, 'order_id');
  const jobIds = relatedJobIds(detail);
  const onfleetTaskId = detailString(detail, 'onfleet_task_id');
  // The short id is the operator-facing handle; the long id is the fallback.
  const onfleetDisplay = detailString(detail, 'onfleet_task_short_id') ?? onfleetTaskId;

  if (!orderDisplay && jobIds.length === 0 && !onfleetDisplay) return null;

  return (
    <section className="space-y-2" aria-label={i18n.t('reviewQueue.detail.relatedEntities')}>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground">
        {i18n.t('reviewQueue.detail.relatedEntities')}
      </h4>
      <dl className="space-y-1 text-sm">
        {orderDisplay && (
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{i18n.t('reviewQueue.detail.relatedOrder')}</dt>
            <dd className="inline-flex items-center gap-1">
              {orderId ? (
                <EntityLink to={`/orders/${orderId}`} className="font-mono text-xs">
                  {orderDisplay}
                </EntityLink>
              ) : (
                <span className="font-mono text-xs text-muted-foreground">{orderDisplay}</span>
              )}
              <CopyButton value={orderDisplay} />
            </dd>
          </div>
        )}
        {jobIds.length > 0 && (
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{i18n.t('reviewQueue.detail.relatedJob')}</dt>
            <dd className="flex flex-wrap gap-2">
              {jobIds.map((jobId) => (
                <IdCell
                  key={jobId}
                  value={jobId}
                  truncate="uuid"
                  linkTo={`/dispatch/jobs/${jobId}`}
                />
              ))}
            </dd>
          </div>
        )}
        {onfleetDisplay && (
          <div className="flex gap-2">
            <dt className="text-muted-foreground">
              {i18n.t('reviewQueue.detail.relatedOnfleetTask')}
            </dt>
            <dd className="font-mono text-xs">{onfleetDisplay}</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ─── Handling guidance + the order's current contents (#1999) ────────────────

/**
 * Cause / kind → the guidance paragraph.
 *
 * TWO maps rather than one, keyed off the SAME closed sets the badges and
 * summaries already use, so a new cause or kind without copy fails at the
 * `Record` exhaustiveness check instead of rendering a blank panel. The cause
 * map wins when the row has a cause: `post_dispatch_cancellation` is a
 * deliberately wide bucket whose four causes need four different actions in
 * two different systems, which is exactly the distinction the operator was
 * missing.
 */
const GUIDANCE_BY_CAUSE: Record<PostDispatchCancellationCause, string> = {
  [POST_DISPATCH_CANCELLATION_CAUSE.QUANTITY_CHANGED_AFTER_DISPATCH]:
    'reviewQueue.guidance.quantity_changed_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.ADDRESS_CHANGED_AFTER_DISPATCH]:
    'reviewQueue.guidance.address_changed_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.SHOPIFY_CANCELLATION_AFTER_DISPATCH]:
    'reviewQueue.guidance.shopify_cancellation_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.ORDER_REFUNDED_AFTER_DISPATCH]:
    'reviewQueue.guidance.order_refunded_after_dispatch',
};
const GUIDANCE_BY_KIND: Record<ReviewQueueKind, string> = {
  [REVIEW_QUEUE_KIND.POST_CUTOFF_ORDER_CHANGE]: 'reviewQueue.guidance.post_cutoff_order_change',
  [REVIEW_QUEUE_KIND.POST_DISPATCH_CANCELLATION]:
    'reviewQueue.guidance.quantity_changed_after_dispatch',
  [REVIEW_QUEUE_KIND.DISPATCH_FAILED]: 'reviewQueue.guidance.dispatch_failed',
};

const guidanceKeyForCause = closedEnumLookup<PostDispatchCancellationCause, string | undefined>(
  GUIDANCE_BY_CAUSE,
  undefined,
);
const guidanceKeyForKind = closedEnumLookup<ReviewQueueKind, string | undefined>(
  GUIDANCE_BY_KIND,
  undefined,
);

/** The guidance key for a row: cause first, kind second, generic last. A
 *  legacy/out-of-set kind lands on the fallback rather than on nothing. */
function guidanceKeyFor(item: ReviewQueueItem): string {
  const byCause = item.cause ? guidanceKeyForCause(item.cause) : undefined;
  return byCause ?? guidanceKeyForKind(item.kind) ?? 'reviewQueue.guidance.fallback';
}

function HandlingGuidance({ item }: { item: ReviewQueueItem }) {
  return (
    <section className="space-y-2" aria-label={i18n.t('reviewQueue.detail.guidance')}>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground">
        {i18n.t('reviewQueue.detail.guidance')}
      </h4>
      <p className="text-sm leading-relaxed text-foreground">{i18n.t(guidanceKeyFor(item))}</p>
    </section>
  );
}

/**
 * What the order says NOW.
 *
 * Deliberately the CURRENT state and not a before/after diff: the
 * `OrderUpdated` event carries only a `lineItemsChanged` boolean and the
 * previous quantities are not persisted anywhere (ADR-0003's minimal
 * cross-context slice), so a diff would need a contract change and would only
 * ever cover rows created after it. The target state is answerable from live
 * data today, and it is what the operator must reconcile Onfleet against.
 *
 * Gated on `order_id`: with no internal id there is nothing to fetch, and
 * `enabled` keeps the query from firing at all rather than firing and failing.
 */
function CurrentOrderContents({ orderId }: { orderId: string | null | undefined }) {
  const { data, isError } = useQuery({
    queryKey: queryKeys.orders.rawDetail(orderId ?? undefined),
    queryFn: () => orderAPI.getOrder(orderId!),
    enabled: Boolean(orderId),
    staleTime: 30_000,
  });

  if (!orderId) return null;
  if (isError) {
    return (
      <p className="text-sm text-muted-foreground">
        {i18n.t('reviewQueue.detail.contentsUnavailable')}
      </p>
    );
  }
  if (!data) return null;

  // `line_items` is top-level on `OrderDetail` (the order's own columns sit
  // under `data.order`). Money is deliberately absent from the table below:
  // the question here is WHAT and HOW MANY, and a price column would invite
  // reading this as the compensation basis, which it is not.
  const items = parseLineItemsJson(data.line_items);

  return (
    <section className="space-y-2" aria-label={i18n.t('reviewQueue.detail.currentContents')}>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground">
        {i18n.t('reviewQueue.detail.currentContents')}
      </h4>
      <p className="text-xs text-muted-foreground">
        {i18n.t('reviewQueue.detail.currentContentsHint')}
      </p>
      <DisplayTable
        ariaLabel={i18n.t('reviewQueue.detail.currentContents')}
        columns={[
          {
            key: 'title',
            header: i18n.t('reviewQueue.col.item'),
            cell: (row) => lineItemTitle(row) ?? '—',
          },
          {
            key: 'quantity',
            header: i18n.t('reviewQueue.col.qty'),
            align: 'right',
            cell: (row) => row.quantity ?? 0,
          },
        ]}
        rows={items}
        rowKey={(row, index) => `${String(lineItemProductId(row) ?? index)}`}
        empty={
          <p className="text-sm text-muted-foreground">{i18n.t('reviewQueue.detail.noPayload')}</p>
        }
      />
    </section>
  );
}

/**
 * The review row's structured payload. A row whose `details_json` is empty says
 * so: the tab exists for every row, so a blank panel would read as a loading
 * failure rather than as "this row carries no payload".
 */
function ReviewQueuePayloadTab({ record }: { record: ReviewQueueItem }) {
  const detail = parseDetails(record.details_json);
  // Guidance leads and needs no payload at all — a row with an empty
  // `details_json` still has an operator asking "what do I do?".
  const guidance = (
    <>
      <HandlingGuidance item={record} />
      <CurrentOrderContents orderId={record.order_id} />
    </>
  );
  if (!record.details_json) {
    return (
      <div className="space-y-4">
        {guidance}
        <p className="text-sm text-muted-foreground">{i18n.t('reviewQueue.detail.noPayload')}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {guidance}
      {detail && <RelatedEntities item={record} detail={detail} />}
      {detail ? (
        <JsonPanel
          value={detail}
          ariaLabel={i18n.t('reviewQueue.detail.payload')}
          label={i18n.t('reviewQueue.detail.payload')}
        />
      ) : (
        <pre className="whitespace-pre-wrap break-all rounded-md border bg-surface-1 p-2 font-mono text-xs">
          {record.details_json}
        </pre>
      )}
    </div>
  );
}

// ─── Model + views ───────────────────────────────────────────────────────────

/**
 * The inbox's two views (#1998).
 *
 *   PENDING — still open AND raised today. A message older than a day has no
 *     claim on the operator's attention, so leaving it in the working list only
 *     buries the ones that do.
 *   HISTORY — every row, any status, any age, newest first. A plain ledger
 *     rather than the strict complement of PENDING: "resolved OR (open AND not
 *     today)" spans two columns and no single backend query expresses it. The
 *     visible consequence is that today's open messages appear in BOTH tabs,
 *     which is ordinary archive semantics (owner decision, 2026-08-07).
 */
export const REVIEW_QUEUE_TAB = {
  PENDING: 'pending',
  HISTORY: 'history',
} as const;
export type ReviewQueueTab = (typeof REVIEW_QUEUE_TAB)[keyof typeof REVIEW_QUEUE_TAB];

// Adapt the `/api/review-queue` list body to the `ListResult` the shared
// ResourcePage consumes — this route names the page size `pageSize`, neither
// the kernel's `size` nor the controllers' `limit` — injecting the tab's
// status view + the whitelisted filters.
function listAdapterFor(tab: ReviewQueueTab) {
  return async function listAdapter(params: DataTableParams): Promise<ListResult<ReviewQueueItem>> {
    const pending = tab === REVIEW_QUEUE_TAB.PENDING;
    const page = await reviewQueueAPI.list({
      status: pending ? REVIEW_QUEUE_STATUS.OPEN : REVIEW_QUEUE_STATUS_FILTER.ALL,
      page: params.page,
      size: params.limit,
      // DataTable runs `manualSorting`, so the header click only reorders rows if
      // the sort reaches the backend; `REVIEW_QUEUE_SORTS` gates which headers are
      // clickable, and the store applies it through `buildOrderBy`.
      sort: params.sort,
      // The QueryBuilder only offers the whitelisted `kind` field, so extraParams
      // carries at most the serialized `kind` condition; the backend validates it.
      //
      // The day bound is computed HERE, per fetch, and never hoisted to module
      // scope: a boundary captured at page-load silently stops meaning "today"
      // the moment the merchant day rolls over under a long-lived tab. An
      // operator-authored `created_at` condition wins — the operator asked a
      // narrower question than the tab's default.
      filters: {
        ...(pending && params.extraParams?.['created_at'] === undefined
          ? pendingViewParams(todayInMerchantTz()).filters
          : {}),
        ...params.extraParams,
      },
    });
    return { items: page.items, total: page.total, page: page.page, limit: page.pageSize };
  };
}

// Enum options drive BOTH the shared QueryBuilder kind filter (ops pinned to
// REVIEW_QUEUE_FILTERS: eq/in) and the model's closed set. Derived from
// `REVIEW_QUEUE_KIND` so a backend rename fails loud rather than dropping a
// filter choice.
const KIND_OPTIONS: EnumOption[] = Object.values(REVIEW_QUEUE_KIND).map((k) => ({
  value: k,
  label: `reviewQueue.kind.${k}`,
}));

const f = createFieldConstructors<ReviewQueueItem>();

// Bound to PENDING for the default bundle; the builder rebinds `list` for the
// history tab (and only then allocates).
const baseApi = { list: listAdapterFor(REVIEW_QUEUE_TAB.PENDING) };

export const reviewQueueModel = defineModel<ReviewQueueItem>()({
  name: 'reviewQueueItem',
  idField: 'id',
  // What an operator calls a row. `recName` has no consumer on this surface yet
  // (the drawer header travels on the host — see
  // `packages/resource-kit-controllers/react/bundle.ts`); this is the value it
  // will resolve to when Stage B wires it up.
  recName: 'summary',
  fields: {
    kind: f.enum({
      label: 'reviewQueue.col.kind',
      options: KIND_OPTIONS,
      widget: 'reviewKindBadge',
    }),
    // `filterable: false` on the three below: each cell shows something other
    // than the value a filter would match (a rebuilt sentence, a formatted
    // timestamp, an opaque id), and `REVIEW_QUEUE_FILTERS` carries `created_at`
    // — so dropping the opt-out would GAIN the operator a filter that answers a
    // different question than the column shows.
    summary: f.text({
      label: 'reviewQueue.col.summary',
      filterable: false,
      widget: 'reviewSummary',
    }),
    created_at: f.datetime({ label: 'reviewQueue.col.createdAt', filterable: false }),
    trace_id: f.id({ label: 'reviewQueue.detail.traceId', filterable: false }),
  },
});

export const reviewQueueListView = defineListView(reviewQueueModel, {
  items: [
    { field: 'kind' },
    { field: 'summary' },
    { field: 'created_at' },
    // Off in the table, present in the drawer — the only surface asymmetry here.
    { field: 'trace_id', columnInvisible: true },
  ],
  // `capabilities.search` is deliberately absent, not `false`: the definition
  // never declared `searchable`, no `REVIEW_QUEUE_SEARCH` constant exists, and
  // the route 400s a free-text `search`. Absent and `false` are different keys.
});

export const reviewQueueFormView = defineFormView(reviewQueueModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'main',
      label: 'reviewQueue.group.main',
      items: [
        { field: 'kind' },
        { field: 'summary' },
        { field: 'trace_id' },
        { field: 'created_at' },
      ],
    },
    // The full structured payload — the former inline `<details>` JSON toggle,
    // now a tab on the read-only drawer so the summary cell never overflows.
    {
      kind: 'custom',
      id: 'payload',
      // Renamed with #1999: the tab now LEADS with what to do and what the
      // order currently says; the raw payload it used to be named for is the
      // last thing in it, and keeps its own `Detail payload` label on the
      // JSON panel.
      label: 'reviewQueue.detail.handling',
      render: ReviewQueuePayloadTab,
    },
  ],
});

/**
 * Build the review-queue resource. Takes the page's `QueryClient` so the resolve
 * action can invalidate the per-order trace views (the framework already
 * invalidates the resource list) — which is why the HOST, unlike the model and
 * the views, cannot lift to module scope.
 */
export function buildReviewQueueResource(
  queryClient: QueryClient,
  tab: ReviewQueueTab = REVIEW_QUEUE_TAB.PENDING,
): ResourceBundle<ReviewQueueItem, typeof reviewQueueModel.fields> {
  const pending = tab === REVIEW_QUEUE_TAB.PENDING;
  const resolveAction = rowAction<ReviewQueueItem, ResolvePayload, ReviewQueueItem>({
    id: 'resolve',
    label: 'reviewQueue.row.resolve',
    // A row with no disposition options cannot be resolved — omit the action.
    // Nor can one that already IS resolved: the store guards its write with
    // `status='open'`, so offering the action on a history row would spend a
    // round trip to earn a 409.
    isEligible: (row) => row.resolveOptions.length > 0 && row.status === REVIEW_QUEUE_STATUS.OPEN,
    confirm: {
      schema: resolvePayloadSchema,
      render: (row, submit, cancel) => (
        <ResolveConfirm item={row} submit={submit} cancel={cancel} />
      ),
    },
    onAction: async (row, payload) => {
      const result = await reviewQueueAPI.resolve(row.id, payload);
      // The framework invalidates the resource list; also refresh the open
      // per-order trace views (they surface `operator_action` from the same
      // table). allSettled so a refetch blip never flips a good resolve.
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.traces() }),
      ]);
      return result;
    },
    // Success moves the row out of the inbox; a 409 means another operator
    // already resolved it (closed-map error override).
    feedback: () => ({ kind: 'success', message: 'reviewQueue.resolve.success.single' }),
    errorFeedback: (err) =>
      err instanceof ApiError && err.code === ERROR_CODES.CONFLICT
        ? 'reviewQueue.resolve.error.alreadyResolved'
        : err instanceof Error
          ? err.message
          : String(err),
  });

  return {
    model: reviewQueueModel,
    list: reviewQueueListView,
    form: reviewQueueFormView,
    host: {
      api: pending ? baseApi : { ...baseApi, list: listAdapterFor(tab) },
      label: 'reviewQueue.pageTitle',
      // DISTINCT per tab. The slug keys the TanStack list cache, so a shared
      // one would let two tabs whose page/sort/filter params happen to match
      // serve each other's rows — the two differ only in arguments the cache
      // key does not see.
      slug: pending ? 'review-queue' : 'review-queue-history',
      listTitle: 'reviewQueue.pageTitle',
      // The inbox is operator-actionable: 5s freshness + a 30s background cadence
      // (refetchIntervalInBackground stays default false).
      staleTime: 5_000,
      refetchInterval: 30_000,
      // The icon only. The count moved onto the Pending TAB (#2004): driven by
      // the rendered list's total it re-read as the size of the archive the
      // moment you switched to history, and a number on the page title claims
      // to be about the page rather than about one of its two views.
      listTitleAdornment: () => <Inbox className="h-6 w-6 text-muted-foreground" />,
      filterable: REVIEW_QUEUE_FILTERS,
      sortable: REVIEW_QUEUE_SORTS,
      // ONE action object behind both surfaces — one busy claim per row.
      rowActions: [resolveAction],
      detail: {
        surface: 'drawer',
        actions: [resolveAction],
        title: () => i18n.t('reviewQueue.detail.title'),
      },
    },
  };
}
